import { getAuth } from 'firebase/auth';

/**
 * Service for interacting with the Java backend API hosted on Firebase Cloud Run
 */
class BackendApiService {
  private apiBaseUrl: string = '';
  private isInitialized: boolean = false;

  constructor() {
    this.initialize();
  }

  /**
   * Initialize the API service by detecting the environment and setting the appropriate API URL
   */
  public async initialize(): Promise<void> {
    try {
      // Determine if we're in development or production environment
      const isLocalDevelopment = window.location.hostname === 'localhost' || 
                                 window.location.hostname === '127.0.0.1';
      
      if (isLocalDevelopment) {
        // In local development, use the local backend
        this.apiBaseUrl = 'http://localhost:8080';
        console.log('Development environment detected. Using local backend API.');
      } else {
        // In production (deployed to Firebase), use the Cloud Run backend
        this.apiBaseUrl = 'https://strategiz-core-bflhiwsnmq-uc.a.run.app';
        console.log('Production environment detected. Using Cloud Run backend API.');
      }
      
      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize Backend API service:', error);
      // Fallback to Cloud Run URL in case of error
      this.apiBaseUrl = 'https://strategiz-core-bflhiwsnmq-uc.a.run.app';
      this.isInitialized = true;
    }
  }

  /**
   * Get authentication headers for API requests
   * @param endpoint The API endpoint being accessed
   * @returns Headers with authentication token if available
   */
  private async getAuthHeaders(endpoint: string = ''): Promise<HeadersInit> {
    // Check if this is an admin endpoint that can work without authentication
    const isAdminEndpoint = endpoint.includes('/admin') || 
                           endpoint.includes('/api/coinbase/api-keys') || 
                           endpoint.includes('/api/coinbase/health');
    
    // For admin endpoints, we can use a simpler approach with email parameter
    if (isAdminEndpoint) {
      console.log('Admin endpoint detected, using simplified authentication');
      return {
        'Content-Type': 'application/json'
      };
    }
    
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      
      if (!currentUser) {
        throw new Error('User not authenticated');
      }
      
      const token = await currentUser.getIdToken();
      
      return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-User-Email': currentUser.email || ''
      };
    } catch (error) {
      console.error('Error getting auth token:', error);
      return {
        'Content-Type': 'application/json'
      };
    }
  }

  /**
   * Make an authenticated request to the backend API
   */
  private async makeRequest(endpoint: string, method: string = 'GET', data?: any): Promise<any> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    const headers = await this.getAuthHeaders(endpoint);
    const url = `${this.apiBaseUrl}${endpoint}`;
    
    const options: RequestInit = {
      method,
      headers,
      credentials: 'include',
    };

    if (data && (method === 'POST' || method === 'PUT')) {
      options.body = JSON.stringify(data);
    }

    try {
      console.log(`Making ${method} request to ${url} with options:`, options);
      const response = await fetch(url, options);
      
      console.log(`Received response with status: ${response.status}`);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Error response body: ${errorText}`);
        throw new Error(`API request failed with status: ${response.status}`);
      }
      
      const responseText = await response.text();
      console.log(`Response body: ${responseText}`);
      
      // If the response is empty, return an empty object
      if (!responseText) {
        return {};
      }
      
      // Try to parse the response as JSON
      try {
        return JSON.parse(responseText);
      } catch (parseError) {
        console.error(`Error parsing response as JSON: ${parseError}`);
        return { text: responseText };
      }
    } catch (error) {
      console.error(`Error making ${method} request to ${endpoint}:`, error);
      throw error;
    }
  }

  /**
   * GET request to the backend API
   */
  public async get<T>(endpoint: string): Promise<T> {
    return this.makeRequest(endpoint, 'GET');
  }

  /**
   * POST request to the backend API
   */
  public async post<T>(endpoint: string, data: any): Promise<T> {
    return this.makeRequest(endpoint, 'POST', data);
  }

  /**
   * PUT request to the backend API
   */
  public async put<T>(endpoint: string, data: any): Promise<T> {
    return this.makeRequest(endpoint, 'PUT', data);
  }

  /**
   * DELETE request to the backend API
   */
  public async delete<T>(endpoint: string): Promise<T> {
    return this.makeRequest(endpoint, 'DELETE');
  }

  /**
   * Get user's API credentials from the backend
   * This leverages the secure credential storage in the Java backend
   */
  public async getUserApiCredentials(platform: string): Promise<any> {
    return this.get(`/api/credentials/${platform}`);
  }

  /**
   * Save user's API credentials securely via the backend
   * This stores credentials in the api_credentials subcollection
   */
  public async saveUserApiCredentials(platform: string, credentials: any): Promise<any> {
    return this.post(`/api/credentials/${platform}`, credentials);
  }
}

export const backendApi = new BackendApiService();
export default backendApi;
